import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@centrix/api-client';
import { Button, Badge, Spinner, Alert, Heading, Paragraph } from '@centrix/shared-ui';

import logoImage from '@/assets/logo.png';
import { errorCircleIcon } from '@centrix/shared-ui/src/icons';

export const LearnerDashboard: FC = () => {
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useCurrentUser();

  const handleLogout = () => {
    // For now, just redirect to login
    // In the future, this should call a logout endpoint
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="text-primary mx-auto mb-4" />
          <Paragraph className="text-gray-600">Loading user profile...</Paragraph>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span
                aria-hidden="true"
                className="h-8 w-8 text-red-500"
                style={{
                  backgroundColor: 'currentColor',
                  maskImage: `url("${errorCircleIcon}")`,
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: 'contain',
                  WebkitMaskImage: `url("${errorCircleIcon}")`,
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: 'contain',
                }}
              />
            </div>
            <Heading as="h2" className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Required
            </Heading>
            <Paragraph className="text-gray-600 mb-6">
              {error.status === 401
                ? 'Please log in to access this page.'
                : 'Unable to load user profile. Please try again.'}
            </Paragraph>
            <Button
              onClick={() => navigate('/login?next=/learner-dashboard')}
              className="w-full h-12 rounded-lg text-base"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <img src={logoImage} alt="Learnovate360" className="h-10 w-auto" />
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-gray-600 hover:text-gray-900 hover:bg-transparent"
          >
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Testing Notice */}
        <Alert variant="warning" title="Testing Page" className="mb-8">
          This is a testing page to verify the authentication flow. It displays user information
          fetched from the{' '}
          <code className="bg-yellow-100 px-1 rounded">/api/centrix/v1/auth/me/</code> endpoint.
        </Alert>

        {/* User Profile Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-purple-600 px-6 py-8">
            <div className="flex items-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-primary text-2xl font-bold">
                {user?.first_name?.[0] || user?.username?.[0] || '?'}
                {user?.last_name?.[0] || ''}
              </div>
              <div className="ml-6 text-white">
                <Heading as="h1" variant="page" className="font-bold text-white">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.username || 'User'}
                </Heading>
                <Paragraph className="text-indigo-200">{user?.email}</Paragraph>
              </div>
            </div>
          </div>

          <div className="px-6 py-6">
            <Heading className="text-lg mb-4">User Information</Heading>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">User ID</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">{user?.id}</dd>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">Username</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">{user?.username}</dd>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">{user?.email}</dd>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">First Name</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {user?.first_name || '—'}
                </dd>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">Last Name</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {user?.last_name || '—'}
                </dd>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">Account Status</dt>
                <dd className="mt-1">
                  <Badge
                    className={
                      user?.is_active
                        ? 'bg-green-100 text-green-800 hover:bg-green-100'
                        : 'bg-red-100 text-red-800 hover:bg-red-100'
                    }
                  >
                    {user?.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </dd>
              </div>
            </dl>
          </div>

          {/* Raw JSON Response */}
          <div className="border-t border-gray-200 px-6 py-6">
            <Heading as="h3" className="text-sm font-medium text-gray-500 mb-2">
              Raw API Response
            </Heading>
            <pre className="bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto text-sm">
              {JSON.stringify(user, null, 2)}
            </pre>
          </div>
        </div>
      </main>
    </div>
  );
};
